import {useEffect, useRef} from 'react'
import {type ImageSourcePropType} from 'react-native'
import QRCodeStyling, {
  type CornerDotType,
  type CornerSquareType,
  type DotType,
} from 'qr-code-styling'

import {useSemanticTokens} from '#/wsocial/service/design-system'

type Props = {
  value: string
  size?: number
  logo?: string
  /** Logo size in pixels (matches native API) */
  logoSize?: number
  /** Override background color; defaults to s.bg.default */
  backgroundColor?: string
  /** Override dot color; defaults to s.text.default */
  color?: string
  // ── Web-only styling ────────────────────────────────────────────────────────
  dotType?: DotType
  cornerSquareType?: CornerSquareType
  cornerDotType?: CornerDotType
  /** Override corner color; defaults to s.button.primary.bg */
  cornersColor?: string
}

export function QrCodeViewer({
  value,
  size = 240,
  logo,
  logoSize = 50,
  backgroundColor,
  color,
  dotType = 'dots',
  cornerSquareType = 'extra-rounded',
  cornerDotType = 'dot',
  cornersColor,
}: Props) {
  const s = useSemanticTokens()
  const containerRef = useRef<HTMLDivElement>(null)

  const bg = backgroundColor ?? s.bg.default
  const dots = color ?? s.text.default
  const corners = cornersColor ?? s.button.primary.bg

  // Extract URI string from ImageSourcePropType (number = bundled asset, skip on web)

  // Convert pixels to qr-code-styling fraction (0–1 relative to QR size)
  const logoFraction =
    logo != null ? (logoSize != null ? logoSize / size : 0.22) : undefined

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const qr = new QRCodeStyling({
      width: size,
      height: size,
      type: 'svg',
      data: value,
      ...(logo ? {image: logo} : {}),
      qrOptions: {
        errorCorrectionLevel: logo ? 'M' : 'M',
      },
      ...(logo
        ? {
            imageOptions: {
              hideBackgroundDots: true,
              imageSize: logoFraction,
              margin: 10,
              crossOrigin: 'anonymous',
            },
          }
        : {}),
      dotsOptions: {
        type: dotType,
        color: dots,
      },
      cornersSquareOptions: {
        type: cornerSquareType,
        color: corners,
      },
      cornersDotOptions: {
        type: cornerDotType,
        color: corners,
      },
      backgroundOptions: {
        color: bg,
        round: 0.067, // ~16px radius at 240px
      },
    })

    el.innerHTML = ''
    qr.append(el)

    return () => {
      el.innerHTML = ''
    }
  }, [
    bg,
    cornerDotType,
    cornerSquareType,
    corners,
    dotType,
    dots,
    logoFraction,
    logo,
    size,
    value,
  ])

  return (
    <div
      ref={containerRef}
      style={{
        width: size,
        height: size,
        borderRadius: 16,
        overflow: 'hidden',
        flexShrink: 0,
        borderColor: dots,
        borderWidth: 4,
        borderStyle: 'solid',
        padding: '10px',
      }}
    />
  )
}
